// ---------------------------------------------------------------------------
// Course Component: Video Player — Progress Tracking
// ---------------------------------------------------------------------------
// Renders video with % watched tracking. Emits events at milestones
// (25%, 50%, 75%, 100%). Supports external URLs (YouTube, Vimeo, Bunny,
// Wistia, Loom) + self-hosted R2.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";
import { resolveVideoEmbed } from "@ease/shared/video-embed";

export interface VideoPlayerProps {
  videoUrl: string;
  /** Source type for correct embed */
  sourceType?: "youtube" | "vimeo" | "bunny" | "wistia" | "loom" | "cloudflare_stream" | "generic" | "r2" | "auto";
  title?: string;
  /** Show progress bar below video */
  showProgress?: boolean;
  /** Mark lesson complete when this % reached */
  completionThreshold?: number;
  /** Auto-play on load */
  autoPlay?: boolean;
  posterUrl?: string;
}

export interface VideoProgressEvent {
  type: "course.video.progressed";
  videoUrl: string;
  percentWatched: number;
  milestone: 25 | 50 | 75 | 100 | null;
  durationSeconds: number;
  watchedSeconds: number;
}

const MILESTONES = [25, 50, 75, 100] as const;
const SANDBOXED_IFRAME_POLICY = "allow-scripts allow-presentation";

/**
 * Detect video source type from URL if not explicitly set.
 */
export function detectSourceType(url: string): VideoPlayerProps["sourceType"] {
  if (!url) return "auto";
  if (isR2VideoUrl(url)) return "r2";
  if (isVimeoUrl(url)) return "vimeo";

  const embed = resolveVideoEmbed(url);
  if (!embed) return "auto";
  if (embed.provider === "cloudflare_stream") return "cloudflare_stream";
  if (embed.provider === "generic") return "generic";
  return embed.provider as Exclude<VideoPlayerProps["sourceType"], "auto" | "r2" | "cloudflare_stream" | "generic">;
}

/**
 * Check if a progress value crosses a milestone.
 */
export function checkMilestone(
  prevPercent: number,
  currentPercent: number,
): (typeof MILESTONES)[number] | null {
  for (const milestone of MILESTONES) {
    if (prevPercent < milestone && currentPercent >= milestone) return milestone;
  }
  return null;
}

/**
 * Calculate completion status for a video.
 */
export function isVideoComplete(
  percentWatched: number,
  threshold: number = 90,
): boolean {
  return percentWatched >= threshold;
}

export const VIDEO_PLAYER_DEF = {
  type: "video-player" as const,
  label: "Video Player",
  category: "course",
  defaultProps: {
    videoUrl: "",
    sourceType: "auto" as const,
    title: "",
    showProgress: true,
    completionThreshold: 90,
    autoPlay: false,
  } satisfies VideoPlayerProps,
};

function isR2VideoUrl(videoUrl: string): boolean {
  try {
    const parsedUrl = new URL(videoUrl);
    return parsedUrl.protocol === "https:"
      && (parsedUrl.hostname.endsWith(".r2.dev") || parsedUrl.hostname.includes("r2.cloudflarestorage"));
  } catch {
    return false;
  }
}

function isVimeoUrl(videoUrl: string): boolean {
  try {
    const parsedUrl = new URL(videoUrl);
    return parsedUrl.protocol === "https:"
      && (
        parsedUrl.hostname === "vimeo.com"
        || parsedUrl.hostname === "www.vimeo.com"
        || parsedUrl.hostname === "player.vimeo.com"
      );
  } catch {
    return false;
  }
}

function renderUnavailableVideo(title: string): string {
  return `<div class="ease-course-video-player__unavailable" role="status">${text(`${title} is unavailable`)}</div>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as VideoPlayerProps;
  const videoUrl = p.videoUrl ?? "";
  const sourceType = p.sourceType === "auto" || !p.sourceType
    ? detectSourceType(videoUrl)
    : p.sourceType;
  const title = p.title?.trim() || "Course video";
  const showProgress = p.showProgress !== false;
  const completionThreshold = p.completionThreshold ?? 90;
  const embed = resolveVideoEmbed(videoUrl);

  const mediaHtml = sourceType === "r2" && isR2VideoUrl(videoUrl)
    ? `<video class="ease-course-video-player__media" controls${p.autoPlay ? " autoplay" : ""}${p.posterUrl ? ` poster="${url(p.posterUrl)}"` : ""}>
      <source src="${url(videoUrl)}">
      ${text(title)}
    </video>`
    : embed
      ? `<iframe
      class="ease-course-video-player__media"
      src="${url(embed.embedUrl)}"
      title="${attr(title)}"
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      ${embed.isSandboxed ? `sandbox="${attr(SANDBOXED_IFRAME_POLICY)}"` : ""}
      allowfullscreen
    ></iframe>`
      : renderUnavailableVideo(title);

  const progressHtml = showProgress
    ? `
    <div class="ease-course-video-player__progress" aria-label="${attr(`Completion threshold ${completionThreshold}%`)}">
      <div class="ease-course-video-player__progress-label">
        <span>Lesson progress</span>
        <span>${text(String(completionThreshold))}% to complete</span>
      </div>
      <div class="ease-course-video-player__progress-track">
        <div class="ease-course-video-player__progress-fill" style="width: 0%"></div>
      </div>
    </div>`
    : "";

  return `<section class="ease-section ease-course-video-player" data-source-type="${attr(sourceType ?? "auto")}">
  <div class="ease-course-video-player__shell">
    <div class="ease-course-video-player__frame">
      ${mediaHtml}
    </div>${progressHtml}
  </div>
</section>`;
}

registerRenderer("video-player", { render });
